import OdxModal from '../../../../OdxComponents/NativeOdx/Modal/Odx-Modal';
import {odxVariants} from '../../../../OdxComponents/constants';
import OdxInputField from '../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField';
import OdxChip from '../../../../OdxComponents/NativeOdx/Odx-Chip';
import * as React from 'react';
import {IAddProject} from "../../Interfaces/IAddProject";
import {IProject} from "../../Interfaces/IAllProjects";
import {createProject} from "../../BackendFacade/apiCalls";
import {isValidEmail} from '../../../../Utils/checker';
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import {simplifyForHtmlId} from "../../../../Utils/transformer";

const AddProject: React.FC<IAddProject> = ({isOpen, closeModal, triggerReload}) => {
    const [projectName, setProjectName] = React.useState<string>('');
    const [projectDescription, setProjectDescription] = React.useState<string>('');
    const [costcenter, setCostcenter] = React.useState<string>('');
    const [internalOrderNumber, setInternalOrderNumber] = React.useState<string>('');
    const [department, setDepartment] = React.useState<string>('');
    const [product, setProduct] = React.useState<string>('');
    const [username, setUsername] = React.useState<string>('');
    const [role, setRole] = React.useState<string>('');
    const [contacts, setContacts] = React.useState<{ mail: string, job: string }[]>([]);
    const baseId = 'hub-add-project';

    const [submitForm, setSubmitForm] = React.useState<boolean>(false);
    const [submitAddContact, setSubmitAddContact] = React.useState<boolean>(false);

    const removeContact = (contact: { mail: string, job: string }) => {
        setContacts(contacts.filter((c) => c !== contact));
    };

    const {createToast, createErrorToast, setLoading} = useMain();

    const createNewProject = () => {
        setSubmitForm(true);
        if (!projectName || !projectDescription || !costcenter || !internalOrderNumber || !department || !product || contacts.length === 0) {
            createErrorToast("Please fill out all fields");
            return;
        }

        const project = {
            contacts: contacts,
            costCenter: costcenter,
            department: department,
            description: projectDescription,
            displayName: projectName,
            product: product,
            projectId: "",
            customProjectIcon: null,
            internalOrderNumber: internalOrderNumber
        } as IProject;

        setLoading(true);

        createProject(project).then((data) => {
            setLoading(false);
            if (data.success) {
                closeModal();
                createToast({message: "Project created successfully", variant: "success", title: "Success"});
            } else {
                createErrorToast("Project could not be created: " + data.message);
            }
        }).finally(() => {
            triggerReload();
        });
    };

    const addContact = () => {
        setSubmitAddContact(true);

        if (username && role && isValidEmail(username)) {
            setContacts([...contacts, {mail: username, job: role}]);
            setSubmitAddContact(false);
            setRole('');
            setUsername('');
        }
    };

    return (
        <>
            <OdxModal
                variant={'sidesheet'}
                onClose={() => closeModal()}
                size={'small'}
                isOpen={isOpen}
                id={baseId}
                footerButtons={[
                    <odx-button
                        key="dismiss"
                        id={`${baseId}-dismiss-btn`}
                        variant="ghost"
                        size="lg"
                        type="button"
                        onClick={() => closeModal()}
                    >
                        Dismiss
                    </odx-button>,
                    <odx-button
                        key="save"
                        id={`${baseId}-save-btn`}
                        variant="primary"
                        size="lg"
                        type="button"
                        onClick={() => createNewProject()}
                    >
                        Save
                    </odx-button>
                ]}
                title={'Add new project'}
            >
                <p className={"text-2xl font-extrabold odx-padding-bottom-8"} id={`${baseId}-project-info-heading`}>Project Info</p>
                <OdxInputField
                    isRequired={true}
                    id={`${baseId}-project-name`}
                    label={'Project name'}
                    value={projectName}
                    onChange={(event) => setProjectName(event.target.value)}
                    error={{isActive: submitForm && !projectName, text: "Please enter a project name"}}
                />
                <OdxInputField
                    isRequired={true}
                    id={`${baseId}-description`}
                    label={'Description'}
                    value={projectDescription}
                    onChange={(event) => setProjectDescription(event.target.value)}
                    error={{isActive: submitForm && !projectDescription, text: "Please enter a description"}}
                />
                <OdxInputField
                    isRequired={true}
                    id={`${baseId}-cost-center`}
                    label={'Costcenter'}
                    value={costcenter}
                    onChange={(event) => setCostcenter(event.target.value)}
                    error={{isActive: submitForm && !costcenter, text: "Please enter a costcenter"}}
                />
                <OdxInputField
                    isRequired={false}
                    id={`${baseId}-internal-order`}
                    label={'Internal Order Number'}
                    value={internalOrderNumber}
                    onChange={(event) => setInternalOrderNumber(event.target.value)}
                    error={{
                        isActive: submitForm && !internalOrderNumber,
                        text: "Please enter an internal order number"
                    }}
                />
                <OdxInputField
                    isRequired={true}
                    id={`${baseId}-department`}
                    label={'Department'}
                    value={department}
                    onChange={(event) => setDepartment(event.target.value)}
                    error={{isActive: submitForm && !department, text: "Please enter a department"}}
                />
                <OdxInputField
                    isRequired={true}
                    id={`${baseId}-product`}
                    label={'Product'}
                    value={product}
                    onChange={(event) => setProduct(event.target.value)}
                    error={{isActive: submitForm && !product, text: "Please enter a product"}}
                />
                <hr/>
                <p className={"text-2xl font-extrabold odx-padding-bottom-8"} id={`${baseId}-contacts-heading`}>Contacts</p>
                <div data-odxLayout={"grid"} className={"odx-padding-bottom-8"} id={`${baseId}-contact-form`}>
                    <div data-odxLayout={"9"}>
                        <OdxInputField
                            isRequired={true}
                            id={`${baseId}-contact-email`}
                            label={'Email'}
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            error={{
                                isActive: submitAddContact && (!username || !isValidEmail(username)),
                                text: "Please enter an email and a valid format"
                            }}
                        />
                        <OdxInputField
                            isRequired={true}
                            id={`${baseId}-contact-role`}
                            label={'Role'}
                            value={role}
                            onChange={(event) => setRole(event.target.value)}
                            error={{isActive: submitAddContact && !role, text: "Please enter a role"}}
                        />
                    </div>
                    <div data-odxLayout={"3"} className={"content-center"}>
                        <odx-button
                            id={`${baseId}-add-contact-btn`}
                            variant="accent"
                            size="lg"
                            type="button"
                            className="mb-0 mt-[5%] float-right"
                            onClick={() => addContact()}
                        >
                            Add
                        </odx-button>
                    </div>
                </div>

                <div>
                    <div className='odxGridContainer flex-wrap' data-odxLayout='flex inline gap-small' id={`${baseId}-contacts-list`}>
                        {contacts.map((user, index) => (
                            <div key={index}>
                                <OdxChip
                                    truncateText={true}
                                    content={user.mail}
                                    contentAddional={user.job}
                                    removable={true}
                                    size='small'
                                    variant={odxVariants.secondary}
                                    onRemove={() => removeContact({mail: user.mail, job: user.job})}
                                    id={`${baseId}-contact-${simplifyForHtmlId(user.mail)}`}
                                />
                            </div>
                        ))}
                    </div>
                    <div>{contacts.length > 0 &&
                        <odx-button
                            variant="ghost"
                            size="lg"
                            type="button"
                            onClick={() => setContacts([])}
                            id={`${baseId}-clear-contacts-btn`}
                        >
                            Clear All
                        </odx-button>}
                    </div>
                </div>
            </OdxModal>
        </>
    );
};

export default AddProject;
